/**
 * Conference Slice - Manages conference session state.
 */

import type { StateCreator } from 'zustand';
import { CallSlice } from '../callSlice/index.types';
import { ConferenceSlice } from './index.types';

export * from './index.types';

type ConferenceSliceCreator = StateCreator<
  ConferenceSlice & CallSlice,
  [],
  [],
  ConferenceSlice
>;

export const createConferenceSlice: ConferenceSliceCreator = (set, get) => ({
  // State
  activeConference: null,

  // Actions
  addConference: (conference) =>
    set({ activeConference: conference }),

  updateConference: (updates) =>
    set((state) => {
      if (!state.activeConference) {
        return state;
      }

      return {
        activeConference: { ...state.activeConference, ...updates },
      };
    }),

  addParticipantToConference: (callId) =>
    set((state) => {
      if (!state.activeConference) {
        return state;
      }

      if (state.activeConference.participantIds.includes(callId)) {
        return state;
      }

      return {
        activeConference: {
          ...state.activeConference,
          participantIds: [...state.activeConference.participantIds, callId],
        },
      };
    }),

  removeParticipantFromConference: (callId) =>
    set((state) => {
      if (!state.activeConference) {
        return state;
      }

      return {
        activeConference: {
          ...state.activeConference,
          participantIds: state.activeConference.participantIds.filter(
            (id) => id !== callId
          ),
        },
      };
    }),

  clearConference: () => {
    const { activeConference, activeCalls } = get();

    if (!activeConference) {
      set({ activeConference: null });
      return;
    }

    // Clear conferenceId from all calls that were participants
    const updatedCalls = new Map(activeCalls);
    for (const [callId, call] of updatedCalls) {
      if (call.conferenceId === activeConference.roomGuid) {
        updatedCalls.set(callId, { ...call, conferenceId: null });
      }
    }

    set({
      activeConference: null,
      activeCalls: updatedCalls,
    });
  },
});
